import { formatDate } from '@seen/core/dates'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { Participant } from '@seen/core/models/patients/participant'

import { cn, DotSeparator } from '@seen/ui'
import { type JSX, memo } from 'react'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { TextLink } from '#src/components/core/TextLink.tsx'
import { PatientName } from '#src/components/patient/PatientName.tsx'
import { useCommentRelatedParticipant } from '#src/hooks/useCommentRelatedParticipant.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useTimelineItemUrl } from '#src/hooks/useTimelineItemUrl.ts'
import { extractTitleFromHTML } from '#src/utils/html.ts'

export interface HoverCommentProps {
  comment: string
  className?: string
}

export const HoverComment = memo(function HoverComment({
  comment: commentReferenceString,
  className,
}: HoverCommentProps): JSX.Element {
  const { data: comment } = useRead(
    CommentCommunication,
    commentReferenceString
  )
  const commentUrl = useTimelineItemUrl(comment)
  const relatedParticipantReference = useCommentRelatedParticipant(comment)
  const { data: relatedParticipant } = useRead(
    Participant,
    relatedParticipantReference
  )

  return (
    <TextLink to={commentUrl} className={cn(className)}>
      <span className="relative bottom-0.25 mr-1 inline-block align-middle">
        <Avatar profile={comment?.practitionerSender} size={3.5} />
      </span>
      {relatedParticipant && (
        <span className="text-foreground-secondary">
          <PatientName participant={relatedParticipant} />
          <DotSeparator />
        </span>
      )}
      {comment?.message && extractTitleFromHTML(comment?.message)}
      {comment?.sentDate && (
        <span className="text-foreground-secondary">
          <DotSeparator />
          {formatDate(comment.sent)}
        </span>
      )}
    </TextLink>
  )
})
